// Comment categories (the Mac's CommentCategoryViews): the project's named, coloured kinds of comment — "Fact-check" in
// red, "Tidy prose" in yellow. A comment's category is shown by its name beside its colour (never by colour alone);
// a writer chooses it on the comment; the sheet names, recolours and deletes them. Colours are classes, one per ink:
// the page's CSP allows no style attributes.
import { useState } from 'react';
import { INK_COLORS, type InkColor, type Project } from '@writing-suite/core';
import { categoryInk, categoryOf, COMMENT_CATEGORY_NAME_LIMIT, type CommentCategory } from '@writing-suite/core/revise';
import type { LiveProject } from '../store/project.ts';
import { addCommentCategory, commentCategoriesOf, deleteCommentCategory, setCommentCategory, updateCommentCategory } from '../store/revise.ts';
import { announce } from '../shell/context.ts';
import { StorySheet } from './story/common.tsx';

const inkName = (ink: string) => ink.charAt(0).toUpperCase() + ink.slice(1);

export function CategoryDot({ category }: { category: Pick<CommentCategory, 'color'> }) {
  return <span className={`category-dot ink-${categoryInk(category)}`} aria-hidden="true" />;
}

/** A comment's category as the margin shows it: its colour and, always, its name. */
export function CategoryTag({ category }: { category: CommentCategory }) {
  return (
    <span className="category-tag" data-category={category.id}>
      <CategoryDot category={category} />
      <span className="visually-hidden">Category: </span>
      {category.name}
    </span>
  );
}

const EDIT = '\u0000edit';

/** Chooses a comment's category (a project's writers). With no categories yet, a link to make some. */
export function CategorySelect({
  comment,
  fragmentId,
  categories,
  live,
  onEdit,
}: {
  comment: { id: string; category: string | null };
  fragmentId: string;
  categories: readonly CommentCategory[];
  live: LiveProject;
  onEdit?: () => void;
}) {
  if (categories.length === 0) {
    return onEdit ? (
      <button type="button" className="link-button" onClick={onEdit}>
        Category…
      </button>
    ) : null;
  }
  const current = categoryOf(comment, categories);
  return (
    <label className="category-select">
      <span className="visually-hidden">Category</span>
      <select
        value={current?.id ?? ''}
        onChange={(e) => {
          const value = e.target.value;
          if (value === EDIT) return onEdit?.();
          setCommentCategory(live, fragmentId, comment.id, value === '' ? null : value);
          announce(value === '' ? 'In no category' : `In ${categories.find((c) => c.id === value)?.name ?? 'the category'}`);
        }}
      >
        <option value="">No category</option>
        {categories.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
        {onEdit ? <option value={EDIT}>Edit categories…</option> : null}
      </select>
    </label>
  );
}

function CategoryRow({ category, live, onError }: { category: CommentCategory; live: LiveProject; onError: (message: string | null) => void }) {
  const [name, setName] = useState(category.name);
  const [confirming, setConfirming] = useState(false);
  const [shown, setShown] = useState(category.name);
  // Renamed elsewhere (another device, or a rename just saved): the field follows.
  if (shown !== category.name) {
    setShown(category.name);
    setName(category.name);
  }
  const rename = () => {
    const next = name.trim();
    if (next === '' || next === category.name) return setName(category.name);
    const refused = updateCommentCategory(live, category.id, { name: next });
    onError(refused);
    if (refused) setName(category.name);
    else announce(`Renamed to ${next}`);
  };
  return (
    <li className="category-row" data-category={category.id}>
      <CategoryDot category={category} />
      <label>
        <span className="visually-hidden">Colour of {category.name}</span>
        <select
          value={categoryInk(category)}
          onChange={(e) => {
            onError(updateCommentCategory(live, category.id, { color: e.target.value as InkColor }));
            announce(`${category.name} is ${e.target.value}`);
          }}
        >
          {INK_COLORS.map((ink) => (
            <option key={ink} value={ink}>
              {inkName(ink)}
            </option>
          ))}
        </select>
      </label>
      <label className="category-name">
        <span className="visually-hidden">Name</span>
        <input
          type="text"
          value={name}
          maxLength={COMMENT_CATEGORY_NAME_LIMIT}
          onChange={(e) => setName(e.target.value)}
          onBlur={rename}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              rename();
            }
          }}
        />
      </label>
      {confirming ? (
        <>
          <button
            type="button"
            className="link-button danger"
            onClick={() => {
              deleteCommentCategory(live, category);
              announce(`Deleted ${category.name}`);
            }}
          >
            Delete it
          </button>
          <button type="button" className="link-button" onClick={() => setConfirming(false)}>
            Keep
          </button>
        </>
      ) : (
        <button type="button" className="link-button" onClick={() => setConfirming(true)} aria-label={`Delete ${category.name}`}>
          Delete
        </button>
      )}
    </li>
  );
}

/** The project's comment categories: each a name and a colour, which every comment in it takes. */
export function CommentCategoriesDialog({ project, live, onClose }: { project: Project; live: LiveProject; onClose: () => void }) {
  const categories = commentCategoriesOf(project);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const add = () => {
    const next = name.trim();
    if (!next) return;
    const refused = addCommentCategory(live, next);
    setError(refused);
    if (!refused) {
      setName('');
      announce(`Added ${next}`);
    }
  };
  return (
    <StorySheet title="Comment Categories" onClose={onClose} className="categories-sheet">
      {categories.length === 0 ? (
        <p className="empty-note">No categories yet.</p>
      ) : (
        <ul className="category-list" aria-label="Categories">
          {categories.map((c) => (
            <CategoryRow key={c.id} category={c} live={live} onError={setError} />
          ))}
        </ul>
      )}
      <p className="sheet-note">
        A comment in a category is underlined in the category’s colour in the text and carries its name in the margin. Renaming or recolouring a category changes
        every comment in it; deleting one leaves its comments in no category.
      </p>
      <form
        className="category-add"
        onSubmit={(e) => {
          e.preventDefault();
          add();
        }}
      >
        <label className="field-label stacked-label" htmlFor="category-new">
          New category
        </label>
        <div className="category-add-row">
          <input id="category-new" type="text" value={name} placeholder="Fact-check" maxLength={COMMENT_CATEGORY_NAME_LIMIT} onChange={(e) => setName(e.target.value)} />
          <button type="submit" className="button secondary small" disabled={!name.trim()}>
            Add
          </button>
        </div>
      </form>
      {error ? (
        <p className="form-error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="actions">
        <button type="button" className="button primary" onClick={onClose}>
          Done
        </button>
      </div>
    </StorySheet>
  );
}
